import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LineChart as LineIcon, Ticket } from "lucide-react";
import { Line, LineChart, ResponsiveContainer } from "recharts";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ranger/PageHeader";
import { BTN_PRIMARY, BTN_SECONDARY } from "@/pages/Servers";
import { api, errMsg, fmtTime } from "@/lib/api";

const inputCls = "border-[#1E2D4A] bg-[#050811] font-mono";

export const LotteryCard = ({ cid, cur, reload }) => {
  const [d, setD] = useState(null);
  const [n, setN] = useState(1);
  const load = useCallback(() => api.get(`/player/${cid}/lottery`).then((r) => setD(r.data)).catch(() => {}), [cid]);
  useEffect(() => { load(); }, [load]);
  const buy = async () => { try { await api.post(`/player/${cid}/lottery`, { count: Number(n) }); toast.success(`Bought ${n} ticket${n > 1 ? "s" : ""}`); load(); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return null;
  return (
    <Card title="Weekly lottery" icon={Ticket} testId="portal-lottery">
      <div className="font-display text-4xl font-bold text-amber-300" data-testid="lottery-prize">{d.prize} <span className="text-base text-slate-400">{cur}</span></div>
      <p className="mt-1 text-xs text-slate-400">{d.tickets} tickets from {d.players} players. Drawn Saturdays 8pm US Central.</p>
      <p data-testid="lottery-mine" className="mt-3 text-sm text-slate-300">You hold <b>{d.my_tickets}</b> tickets ({d.my_chance}% chance).</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Input data-testid="lottery-count" type="number" min={1} max={100} value={n} onChange={(e) => setN(e.target.value)} className={`w-24 ${inputCls}`} />
        <button data-testid="lottery-buy" disabled={!(n >= 1 && n <= 100)} onClick={buy} className={BTN_PRIMARY}>Buy for {n * d.ticket_price} {cur}</button>
      </div>
      {d.last && <p className="mt-3 text-xs text-slate-500">Last winner: {d.last.winner} won {d.last.prize} {cur} · {fmtTime(d.last.at)}</p>}
    </Card>
  );
};

const Spark = ({ data, up }) => (
  <div className="h-8 w-24"><ResponsiveContainer><LineChart data={data.map((v, i) => ({ i, v }))}><Line type="monotone" dataKey="v" dot={false} strokeWidth={1.5} stroke={up ? "#34D399" : "#F87171"} isAnimationActive={false} /></LineChart></ResponsiveContainer></div>
);

const StockRow = ({ s, held, trade }) => {
  const [n, setN] = useState(1);
  const up = s.change_pct >= 0;
  return (
    <li data-testid={`stock-${s.ticker}`} className="flex flex-wrap items-center gap-3 border-t border-[#1E2D4A] py-2.5 text-sm">
      <div className="w-36"><div className="font-mono font-bold text-slate-100">{s.ticker}</div><div className="text-xs text-slate-500">{s.name}</div></div>
      <Spark data={s.history} up={up} />
      <div className="w-28 font-mono"><div className="text-slate-100">{s.price.toFixed(2)}</div><div className={`text-xs ${up ? "text-emerald-400" : "text-red-400"}`}>{up ? "▲" : "▼"} {Math.abs(s.change_pct)}%</div></div>
      <div className="w-20 text-xs text-slate-400">Own {held?.shares || 0}</div>
      <Input data-testid={`stock-shares-${s.ticker}`} type="number" min={1} value={n} onChange={(e) => setN(e.target.value)} className={`w-20 ${inputCls}`} />
      <button data-testid={`stock-buy-${s.ticker}`} onClick={() => trade(s.ticker, "buy", n)} className={BTN_PRIMARY}>Buy</button>
      <button data-testid={`stock-sell-${s.ticker}`} disabled={!held?.shares} onClick={() => trade(s.ticker, "sell", n)} className={BTN_SECONDARY}>Sell</button>
    </li>
  );
};

export const StocksCard = ({ cid, cur, reload }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/player/${cid}/stocks`).then((r) => setD(r.data)).catch(() => {}), [cid]);
  useEffect(() => { load(); }, [load]);
  const trade = async (t, side, n) => { try { const { data } = await api.post(`/player/${cid}/stocks/${t}/${side}`, { shares: Number(n) }); toast.success(`${side === "buy" ? "Bought" : "Sold"} ${n} ${t} for ${data.total} ${cur}`); load(); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return null;
  const held = Object.fromEntries(d.holdings.map((h) => [h.ticker, h]));
  const worth = d.holdings.reduce((a, h) => a + h.value, 0);
  return (
    <Card title="Stock market" icon={LineIcon} testId="portal-stocks">
      <p className="mb-2 text-xs text-slate-400">Prices move every hour. {d.fee_pct}% fee on each trade. Portfolio value: <b data-testid="stocks-worth" className="text-slate-200">{worth} {cur}</b></p>
      <ul>{d.market.map((s) => <StockRow key={s.ticker} s={s} held={held[s.ticker]} trade={trade} />)}</ul>
    </Card>
  );
};
